/**
 * First-touch acquisition capture. Writes the `ex_acq` cookie from UTM
 * parameters (or an external referrer) and reads it back at signup.
 *
 * Keep in sync with the other copy (apps/landing/lib/acquisition.ts); apps
 * cannot import each other, so constants and behavior are mirrored by hand.
 *
 * Attribution is best-effort metadata: nothing here may throw or block UI.
 */

export interface Acquisition {
  source: string;
  medium?: string;
  campaign?: string;
  term?: string;
  content?: string;
  /** ISO timestamp of the first touch. */
  capturedAt?: string;
}

export const ACQUISITION_COOKIE = 'ex_acq';
export const OWN_HOST_SUFFIXES = ['examing.uz', 'examing.app'];
export const IGNORED_REFERRER_HOSTS = [
  'accounts.google.com',
  'oauth.telegram.org',
  'checkout.paycom.uz',
  'checkout.test.paycom.uz',
  'test.paycom.uz',
  'my.click.uz',
];

const TELEGRAM_HOSTS = ['t.me', 'telegram.org', 'web.telegram.org'];
const FIELD_MAX_LENGTH = 100;
const COOKIE_MAX_AGE_SECONDS = 90 * 24 * 60 * 60;
/** Browsers cap a cookie at ~4 KB; anything larger was not written by us. */
const COOKIE_VALUE_MAX_LENGTH = 4096;
/** The API treats a longer `acq` query value as absent. */
const QUERY_PARAM_MAX_LENGTH = 1024;
const CONTROL_CHARS = new RegExp('\\p{C}', 'gu');

interface CookiePayload {
  s: string;
  m?: string;
  c?: string;
  t?: string;
  n?: string;
  ts?: number;
}

export interface CaptureEnv {
  search: string;
  referrer: string;
  hostname: string;
  protocol: string;
  getCookie(): string | undefined;
  setCookie(value: string): void;
  now(): number;
}

function clean(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const cleaned = value.slice(0, FIELD_MAX_LENGTH).replace(CONTROL_CHARS, '').trim();
  return cleaned.length > 0 ? cleaned : undefined;
}

function toIso(epochMs: unknown): string | undefined {
  if (typeof epochMs !== 'number') return undefined;
  const date = new Date(epochMs);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

function hostMatches(host: string, domain: string): boolean {
  return host === domain || host.endsWith(`.${domain}`);
}

/** Lowercase, drop one trailing FQDN dot (`examing.uz.`) and a leading `www.`. */
function comparableHost(hostname: string): string {
  return hostname.toLowerCase().replace(/\.$/, '').replace(/^www\./, '');
}

function referrerHost(referrer: string): string | undefined {
  try {
    const url = new URL(referrer);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return undefined;
    const host = comparableHost(url.hostname);
    return host.length > 0 ? host : undefined;
  } catch {
    return undefined;
  }
}

function isIgnoredReferrer(host: string, hostname: string): boolean {
  return (
    host === comparableHost(hostname) ||
    OWN_HOST_SUFFIXES.some((suffix) => hostMatches(host, suffix)) ||
    IGNORED_REFERRER_HOSTS.includes(host)
  );
}

export function deriveAcquisition(input: {
  search: string;
  referrer: string;
  hostname: string;
  now: number;
}): Acquisition | undefined {
  const capturedAt = toIso(input.now);
  const params = new URLSearchParams(input.search);

  const source = clean(params.get('utm_source'));
  if (source !== undefined) {
    const medium = clean(params.get('utm_medium'));
    const campaign = clean(params.get('utm_campaign'));
    const term = clean(params.get('utm_term'));
    const content = clean(params.get('utm_content'));
    return {
      source,
      ...(medium !== undefined ? { medium } : {}),
      ...(campaign !== undefined ? { campaign } : {}),
      ...(term !== undefined ? { term } : {}),
      ...(content !== undefined ? { content } : {}),
      ...(capturedAt !== undefined ? { capturedAt } : {}),
    };
  }

  const host = referrerHost(input.referrer);
  if (host === undefined || isIgnoredReferrer(host, input.hostname)) return undefined;

  return {
    source: TELEGRAM_HOSTS.includes(host) ? 'telegram' : host,
    medium: 'referral',
    ...(capturedAt !== undefined ? { capturedAt } : {}),
  };
}

/**
 * The trailing FQDN dot is deliberately NOT stripped here: browsers reject
 * `Domain=.examing.uz` on the host `examing.uz.`, so such a host keeps a
 * host-only cookie instead of none.
 */
export function acquisitionCookieDomain(hostname: string): string | undefined {
  const host = hostname.toLowerCase();
  const suffix = OWN_HOST_SUFFIXES.find((candidate) => hostMatches(host, candidate));
  return suffix === undefined ? undefined : `.${suffix}`;
}

function readCookieValue(cookieString: string): string | undefined {
  const prefix = `${ACQUISITION_COOKIE}=`;
  const entry = cookieString
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));
  return entry === undefined ? undefined : entry.slice(prefix.length);
}

export function readAcquisition(cookieString?: string): Acquisition | undefined {
  try {
    const raw = readCookieValue(cookieString ?? document.cookie);
    if (raw === undefined || raw.length > COOKIE_VALUE_MAX_LENGTH) return undefined;

    const parsed: unknown = JSON.parse(decodeURIComponent(raw));
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return undefined;

    const payload = parsed as Record<string, unknown>;
    const source = clean(payload.s);
    if (source === undefined) return undefined;

    const medium = clean(payload.m);
    const campaign = clean(payload.c);
    const term = clean(payload.t);
    const content = clean(payload.n);
    const capturedAt = toIso(payload.ts);
    return {
      source,
      ...(medium !== undefined ? { medium } : {}),
      ...(campaign !== undefined ? { campaign } : {}),
      ...(term !== undefined ? { term } : {}),
      ...(content !== undefined ? { content } : {}),
      ...(capturedAt !== undefined ? { capturedAt } : {}),
    };
  } catch {
    return undefined;
  }
}

function buildCookie(acquisition: Acquisition, env: CaptureEnv): string {
  const payload: CookiePayload = {
    s: acquisition.source,
    ...(acquisition.medium !== undefined ? { m: acquisition.medium } : {}),
    ...(acquisition.campaign !== undefined ? { c: acquisition.campaign } : {}),
    ...(acquisition.term !== undefined ? { t: acquisition.term } : {}),
    ...(acquisition.content !== undefined ? { n: acquisition.content } : {}),
    ts: env.now(),
  };
  const domain = acquisitionCookieDomain(env.hostname);
  return [
    `${ACQUISITION_COOKIE}=${encodeURIComponent(JSON.stringify(payload))}`,
    'Path=/',
    `Max-Age=${COOKIE_MAX_AGE_SECONDS}`,
    'SameSite=Lax',
    ...(domain !== undefined ? [`Domain=${domain}`] : []),
    ...(env.protocol === 'https:' ? ['Secure'] : []),
  ].join('; ');
}

function browserEnv(): CaptureEnv {
  return {
    search: window.location.search,
    referrer: document.referrer,
    hostname: window.location.hostname,
    protocol: window.location.protocol,
    getCookie: () => document.cookie,
    setCookie: (value) => {
      document.cookie = value;
    },
    now: () => Date.now(),
  };
}

export function captureAcquisition(env?: CaptureEnv): void {
  try {
    const resolved = env ?? browserEnv();
    if (readAcquisition(resolved.getCookie() ?? '') !== undefined) return;

    const acquisition = deriveAcquisition({
      search: resolved.search,
      referrer: resolved.referrer,
      hostname: resolved.hostname,
      now: resolved.now(),
    });
    if (acquisition === undefined) return;

    resolved.setCookie(buildCookie(acquisition, resolved));
  } catch {
    // Cookies can be blocked; attribution must never break the page.
  }
}

/**
 * Sending is OFF unless REACT_APP_SEND_ACQUISITION is exactly 'true'. The API
 * rejects an unknown `acquisition` body field (forbidNonWhitelisted), and web
 * and API deploy independently, so web ships with sending off and the flag is
 * switched on only after the API change is live. Capture stays always on
 * (it only writes a cookie). See docs/ACQUISITION.md, "Rollout and rollback".
 * Read at call time; CRA inlines the expression at build time.
 */
function isSendingEnabled(): boolean {
  return process.env.REACT_APP_SEND_ACQUISITION === 'true';
}

/**
 * Spread into a signup request body; `{}` when nothing was captured or
 * sending is disabled.
 */
export function acquisitionPayload(): { acquisition?: Acquisition } {
  if (!isSendingEnabled()) return {};
  const acquisition = readAcquisition();
  return acquisition === undefined ? {} : { acquisition };
}

/**
 * JSON for the Google redirect's `acq` query param. Dropped (never truncated)
 * when it exceeds the API's cap, since cut-off JSON would be unparseable.
 * `undefined` when sending is disabled.
 */
export function acquisitionQueryParam(): string | undefined {
  if (!isSendingEnabled()) return undefined;
  const acquisition = readAcquisition();
  if (acquisition === undefined) return undefined;
  const json = JSON.stringify(acquisition);
  return json.length > QUERY_PARAM_MAX_LENGTH ? undefined : json;
}
